import { FC, memo, useMemo } from "react";
import { MatchStatus } from "service";
import { getTeamColors } from "components/colors";
import { SIDE } from "_constants";
import {
  TENNIS_VIEW_H,
  TENNIS_VIEW_W,
  resolveTennisPosition,
} from "../positioning";

const SERVICE_CODE = "1113";
const INDICATOR_SCALE = 0.52;
const VW_EDGE_OFFSET_PX = 0.25 * TENNIS_VIEW_W; // push indicator closer to the sidelines
const VH_OFFSET_HOME_PX = INDICATOR_SCALE * 110;
const VH_OFFSET_AWAY_PX = INDICATOR_SCALE * 130; // aligns fan center with resolved position

const getNormalizedCode = (status: MatchStatus): string | undefined => {
  const raw = status as any;
  const nsc = raw?.nsc;
  if (typeof nsc === "string") return nsc.slice(-4);
  if (typeof nsc === "number") return nsc.toString().slice(-4);

  const sc = raw?.sc;
  if (typeof sc === "string") return sc.slice(-4);
  if (typeof sc === "number") return sc.toString().slice(-4);

  return undefined;
};

type ServiceIndicatorProps = {
  status: MatchStatus;
  normalizedCode?: string;
};

export const ServiceIndicator: FC<ServiceIndicatorProps> = memo(
  ({ status, normalizedCode }) => {
    const code = useMemo(
      () => normalizedCode ?? getNormalizedCode(status),
      [normalizedCode, status]
    );

    if (code !== SERVICE_CODE) return null;

    const { x, y, side } = resolveTennisPosition(status);

    const toPixel = (value: number, max: number) => {
      if (!Number.isFinite(value)) return 0;
      const isNormalized = value >= 0 && value <= 1;
      const raw = isNormalized ? value * max : value;
      return Math.min(Math.max(raw, 0), max);
    };

    const normalizedX = toPixel(x, TENNIS_VIEW_W);
    const offsetDirection =
      side !== SIDE.NO_SIDE
        ? side
        : normalizedX >= TENNIS_VIEW_W / 2
        ? SIDE.AWAY
        : SIDE.HOME;
    const edgeOffset =
      offsetDirection === SIDE.AWAY ? VW_EDGE_OFFSET_PX : -VW_EDGE_OFFSET_PX;
    const xPx = Math.min(Math.max(normalizedX + edgeOffset, 0), TENNIS_VIEW_W);
    const verticalOffset =
      side === SIDE.AWAY
        ? VH_OFFSET_AWAY_PX
        : side === SIDE.HOME
        ? VH_OFFSET_HOME_PX
        : normalizedX >= TENNIS_VIEW_W / 2
        ? VH_OFFSET_AWAY_PX
        : VH_OFFSET_HOME_PX;
    const yPx = Math.min(
      Math.max(toPixel(y, TENNIS_VIEW_H) - verticalOffset, 0),
      TENNIS_VIEW_H
    );
    const rotation =
      side === SIDE.HOME ? -90 : side === SIDE.AWAY ? 90 : x >= 0.5 ? -90 : 90;

    const { home, away } = getTeamColors(status as any);
    const fallbackColor = x >= 0.5 ? away : home;
    const fillColor =
      side === SIDE.AWAY ? away : side === SIDE.HOME ? home : fallbackColor;

    return (
      <div
        className="pointer-events-none absolute inset-0 z-[999999998] flex items-center justify-center"
        style={{ transformStyle: "preserve-3d" }}
      >
        <div className="pointer-events-none absolute inset-0">
          <svg
            className="h-full w-full overflow-visible"
            viewBox={`0 0 ${TENNIS_VIEW_W} ${TENNIS_VIEW_H}`}
          >
            <g
              transform={`translate(${xPx} ${yPx}) rotate(${rotation}) scale(${INDICATOR_SCALE})`}
              style={{ transformBox: "fill-box", transformOrigin: "center" }}
            >
              <path
                opacity="0.8"
                d="m 0 0, -18.125 36.25 c 0,0,18.125,9.0625, 36.25 0"
                fill={fillColor}
              ></path>
              <path
                opacity="0.4"
                d="m 0 0, -36.875 73.75 c 0,0,36.875,18.4375, 73.75 0"
                fill={fillColor}
              ></path>
              <path
                opacity="0.3"
                d="m 0 0, -55.625 111.25 c 0,0,55.625,27.8125, 111.25 0"
                fill={fillColor}
              ></path>
            </g>
          </svg>
        </div>
      </div>
    );
  }
);
